import type { AppointmentItem, CalendarView, ExternalEvent, RoleItem, TaskItem, WorkOrderListItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { del, errorMessage, get, patch, post } from "../api";
import { ExternalDialog, asItem, isExternal } from "../components/external-events";
import { AppointmentDialog, TaskDialog, WeatherFlag, appointmentWhen, toDateInput } from "../components/schedule";
import { Alert, Button, Checkbox, Dialog, Empty, Field, Loading, PageHeader, Panel, SelectField, cx } from "../components/ui";
import { useUsers } from "../lookups";
import { useSession } from "../session";

type View = "month" | "week" | "day" | "list" | "crews";
const DAY_START = 6;
const DAY_END = 21;
const HOUR_PX = 48;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameDay = (a: Date, b: Date) => toDateInput(a) === toDateInput(b);
/** Weeks start on Sunday. */
const startOfWeek = (d: Date) => addDays(startOfDay(d), -d.getDay());

/** Tasks with a due date sit in the all-day row of their due day, with a box to tick. */
const isTask = (a: AppointmentItem) => a.id.startsWith("task:");
const taskOf = (a: AppointmentItem) => (a as AppointmentItem & { task?: TaskItem }).task;
function taskAsItem(t: TaskItem): AppointmentItem {
  const day = startOfDay(new Date(t.dueAt!)).toISOString();
  return {
    id: `task:${t.id}`,
    title: t.title,
    type: "",
    startsAt: day,
    endsAt: day,
    allDay: true,
    location: "",
    notes: "",
    attendees: [t.assignee, ...t.alsoAssigned].map((u) => ({ ...u, googleSynced: false, googleError: null })),
    createdBy: null,
    weather: null,
    jobId: t.jobId,
    jobNumber: t.jobNumber,
    contactId: t.contactId,
    contactName: t.contactName,
    task: t,
  } as unknown as AppointmentItem;
}
/** "3pm", or nothing for a task due at midnight (the day only). */
const dueTime = (t: TaskItem) => {
  const d = new Date(t.dueAt!);
  return d.getHours() || d.getMinutes() ? time(t.dueAt!) : "";
};

function TaskBox({ t }: { t: TaskItem }) {
  const qc = useQueryClient();
  const tick = useMutation({
    mutationFn: () => patch(`/api/tasks/${t.id}`, { done: !t.completedAt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  return (
    <input
      type="checkbox"
      checked={!!t.completedAt}
      disabled={tick.isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={() => tick.mutate()}
      aria-label={t.completedAt ? `Reopen ${t.title}` : `Mark ${t.title} done`}
      className="size-3.5 shrink-0 accent-[var(--color-brand)]"
    />
  );
}

function range(view: View, anchor: Date): [Date, Date] {
  if (view === "day") return [startOfDay(anchor), addDays(startOfDay(anchor), 1)];
  if (view === "week") return [startOfWeek(anchor), addDays(startOfWeek(anchor), 7)];
  if (view === "list") return [startOfDay(anchor), addDays(startOfDay(anchor), 30)];
  if (view === "crews") return [startOfWeek(anchor), addDays(startOfWeek(anchor), 14)];
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  return [startOfWeek(first), addDays(startOfWeek(first), 42)];
}

function title(view: View, anchor: Date) {
  if (view === "month") return anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  if (view === "day") return anchor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const [from, to] = range(view, anchor);
  const last = addDays(to, -1);
  return `${from.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${last.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

export const GOOGLE_RESULTS: Record<string, { tone: "ok" | "danger"; text: string }> = {
  connected: { tone: "ok", text: "Google Calendar connected. Your appointments will show up there." },
  cancelled: { tone: "danger", text: "Google Calendar wasn't connected." },
  "wrong-account": { tone: "danger", text: "That's a different Google account than the one you sign in with. Connect your own calendar." },
  refused: { tone: "danger", text: "Google didn't allow calendar access. Try again and allow it." },
  expired: { tone: "danger", text: "That took too long. Try again." },
  "signed-out": { tone: "danger", text: "You were signed out. Sign in and try again." },
  unavailable: { tone: "danger", text: "Google Calendar isn't available here." },
};

export function CalendarPage() {
  const { can, me } = useSession();
  const qc = useQueryClient();
  const users = useUsers();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<View>(() => (window.innerWidth < 640 ? "list" : "week"));
  // ?date=YYYY-MM-DD opens on that day (the dashboard's "See more").
  const [anchor, setAnchor] = useState(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(params.get("date") ?? "");
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : startOfDay(new Date());
  });
  const [people, setPeople] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [viewId, setViewId] = useState("");
  const [saving, setSaving] = useState(false);
  const saved = me?.calendarViews ?? [];
  const apply = (v: CalendarView | undefined) => {
    setViewId(v?.id ?? "");
    setTypes(v?.types ?? []);
    setPeople(v?.userIds ?? []);
    if (v) setView(window.innerWidth < 640 && v.view !== "crews" ? "list" : v.view);
  };
  // The view set as the default for one of my roles, once.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (started || !me) return;
    setStarted(true);
    apply(me.calendarViews.find((v) => v.roleIds.some((r) => me.user.roleIds.includes(r))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, started]);
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const [adding, setAdding] = useState<Date | null>(null);
  const [from, to] = range(view, anchor);

  const qs = new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), ...(people.length ? { users: people.join(",") } : {}), ...(types.length ? { type: types.join(",") } : {}) });
  const appts = useQuery({ queryKey: ["appointments", "range", qs.toString()], queryFn: () => get<AppointmentItem[]>(`/api/appointments?${qs}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["appointments"] });
  // Other calendars (yours, and teammates' they share), drawn alongside appointments.
  const extQs = new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), ...(people.length ? { users: people.join(",") } : {}) });
  const ext = useQuery({ queryKey: ["external-events", extQs.toString()], queryFn: () => get<ExternalEvent[]>(`/api/calendar/external?${extQs}`), enabled: view !== "crews" && !types.length, staleTime: 60_000 });
  const [openExt, setOpenExt] = useState<ExternalEvent | null>(null);
  // Tasks due in the window: mine, or the picked people's (if I can see others' tasks).
  const [showTasks, setShowTasks] = useState(() => {
    try {
      return localStorage.getItem("calendar.tasks") !== "off";
    } catch {
      return true;
    }
  });
  const toggleTasks = () => {
    setShowTasks(!showTasks);
    try {
      localStorage.setItem("calendar.tasks", showTasks ? "off" : "on");
    } catch {
      // Not remembered; fine.
    }
  };
  const taskPeople = can("tasks.manage_all") ? people : [];
  const taskQs = new URLSearchParams({ status: "all", dueFrom: from.toISOString(), dueTo: to.toISOString(), limit: "500", ...(taskPeople.length ? { users: taskPeople.join(",") } : {}) });
  const tasks = useQuery({ queryKey: ["tasks", "calendar", taskQs.toString()], queryFn: () => get<TaskItem[]>(`/api/tasks?${taskQs}`), enabled: showTasks && view !== "crews" && !types.length });
  const [openTask, setOpenTask] = useState<TaskItem | null>(null);
  const items = useMemo(
    () => [...(appts.data ?? []), ...(ext.data ?? []).map(asItem), ...(showTasks && !types.length ? (tasks.data ?? []).filter((t) => t.dueAt).map(taskAsItem) : [])].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [appts.data, ext.data, tasks.data, showTasks, types.length],
  );
  const openItem = (a: AppointmentItem) => {
    const t = taskOf(a);
    if (t) return setOpenTask(t);
    const e = isExternal(a) ? ext.data?.find((x) => `ext:${x.id}` === a.id) : null;
    if (e) setOpenExt(e);
    else if (!isExternal(a)) setOpen(a);
  };

  const step = (dir: number) => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    else setAnchor(addDays(anchor, dir * (view === "week" ? 7 : view === "crews" ? 14 : view === "list" ? 30 : 1)));
  };
  const colorOf = (a: AppointmentItem) => (isExternal(a) ? a.attendees[0]!.color : people.length === 1 ? (a.attendees.find((x) => x.id === people[0])?.color ?? a.attendees[0]?.color) : a.attendees[0]?.color) ?? "#888888";
  const googleResult = GOOGLE_RESULTS[params.get("google") ?? ""];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Calendar"
        action={
          can("appointments.edit") && (
            <Button variant="primary" onClick={() => setAdding(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 9))}>
              + New appointment
            </Button>
          )
        }
      />
      {googleResult && (
        <Alert tone={googleResult.tone}>
          {googleResult.text}{" "}
          <button className="font-semibold underline" onClick={() => setParams({}, { replace: true })}>
            Dismiss
          </button>
        </Alert>
      )}
      <GoogleBar />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button className="px-3 py-1.5" onClick={() => setAnchor(startOfDay(new Date()))}>
            Today
          </Button>
          <Button className="px-2.5 py-1.5" onClick={() => step(-1)} aria-label="Previous">
            ‹
          </Button>
          <Button className="px-2.5 py-1.5" onClick={() => step(1)} aria-label="Next">
            ›
          </Button>
          <h2 className="ml-1 text-lg font-semibold">{title(view, anchor)}</h2>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <SelectField label="Saved view" value={viewId} onChange={(e) => apply(saved.find((v) => v.id === e.target.value))} className="min-w-36">
            <option value="">Everything</option>
            {saved.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </SelectField>
          {view !== "crews" && (
            <SelectField label="Type" value={types.length > 1 ? "*" : (types[0] ?? "")} onChange={(e) => e.target.value !== "*" && setTypes(e.target.value ? [e.target.value] : [])} className="min-w-40">
              <option value="">All types</option>
              {types.length > 1 && <option value="*">{types.join(", ")}</option>}
              {me?.lists.appointmentTypes.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </SelectField>
          )}
          {view !== "crews" && (
            <Checkbox label="Tasks" checked={showTasks} onChange={toggleTasks} />
          )}
          {can("settings.manage") && (
            <Button className="px-3 py-1.5" onClick={() => setSaving(true)}>
              Save view
            </Button>
          )}
          <div role="radiogroup" aria-label="View" className="inline-flex rounded-[var(--radius-control)] bg-mist p-0.5">
            {(["month", "week", "day", "list", "crews"] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={view === v}
                onClick={() => setView(v)}
                className={cx("rounded-[6px] px-3 py-1.5 text-sm font-semibold capitalize", view === v ? "bg-white shadow-sm" : "text-muted hover:text-ink")}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={cx("flex flex-wrap gap-1.5", view === "crews" && "hidden")} aria-label="People">
        <button
          type="button"
          aria-pressed={!people.length}
          onClick={() => setPeople([])}
          className={cx("rounded-full px-3 py-1 text-sm font-medium", !people.length ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}
        >
          Everyone
        </button>
        {users.data
          ?.filter((u) => u.active)
          .map((u) => {
            const on = people.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                aria-pressed={on}
                onClick={() => setPeople(on ? people.filter((x) => x !== u.id) : [...people, u.id])}
                className={cx("flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium", on ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}
              >
                <span className="size-2 rounded-full" style={{ background: u.color }} />
                {u.name}
              </button>
            );
          })}
      </div>

      {appts.error ? (
        <Alert>{errorMessage(appts.error)}</Alert>
      ) : appts.isLoading ? (
        <Loading />
      ) : view === "crews" ? (
        <CrewView from={from} to={to} />
      ) : view === "month" ? (
        <MonthView anchor={anchor} from={from} items={items} colorOf={colorOf} onOpen={openItem} onAdd={can("appointments.edit") ? setAdding : undefined} onDay={(d) => (setAnchor(d), setView("day"))} />
      ) : view === "list" ? (
        <ListView items={items} colorOf={colorOf} onOpen={openItem} />
      ) : (
        <TimeGrid days={view === "day" ? [anchor] : Array.from({ length: 7 }, (_, i) => addDays(from, i))} items={items} colorOf={colorOf} onOpen={openItem} onAdd={can("appointments.edit") ? setAdding : undefined} />
      )}
      {openExt && <ExternalDialog e={openExt} onClose={() => setOpenExt(null)} />}
      {openTask && <TaskDialog task={openTask} onClose={() => setOpenTask(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["tasks"] })} />}

      {adding && <AppointmentDialog start={adding} onClose={() => setAdding(null)} onSaved={refresh} />}
      {saving && <SaveViewDialog current={saved.find((v) => v.id === viewId)} config={{ view, types, userIds: people }} views={saved} onClose={() => setSaving(false)} onSaved={(id) => (setSaving(false), setViewId(id))} />}
      {open && <AppointmentDialog appointment={open} onClose={() => setOpen(null)} onSaved={refresh} />}
    </div>
  );
}

/** Connect / status for the signed-in person's own Google Calendar. */
/** Google Calendar: connect, sync now, or disconnect. `returnTo`: where Google sends you back. */
export function GoogleBar({ returnTo = "/calendar", sayIfUnavailable = false }: { returnTo?: string; sayIfUnavailable?: boolean } = {}) {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["google-calendar"],
    queryFn: () => get<{ available: boolean; connected: boolean; email: string | null; error: string | null }>("/api/me/google-calendar"),
  });
  const disconnect = useMutation({ mutationFn: () => del("/api/me/google-calendar"), onSuccess: () => qc.invalidateQueries({ queryKey: ["google-calendar"] }) });
  const sync = useMutation({ mutationFn: () => post<{ synced: number }>("/api/me/google-calendar/sync"), onSuccess: () => qc.invalidateQueries({ queryKey: ["appointments"] }) });
  const s = status.data;
  if (!s?.available) return sayIfUnavailable && s ? <span className="text-sm text-muted">Google Calendar isn't set up on this server.</span> : null;
  const connect = () => (window.location.href = `/api/auth/google/calendar?returnTo=${encodeURIComponent(returnTo)}`);
  if (!s.connected) {
    return (
      <Panel className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <span className="text-sm text-ink-soft">{s.error ? `Your Google Calendar stopped syncing: ${s.error}` : "Put your appointments on your Google Calendar automatically."}</span>
        <Button className="px-3 py-1.5" onClick={connect}>
          Connect Google Calendar
        </Button>
      </Panel>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
      <span>Syncing to {s.email}'s Google Calendar.</span>
      <Link to="/profile" className="font-medium hover:text-ink">
        Calendars…
      </Link>
      <button className="font-medium hover:text-ink" onClick={() => sync.mutate()} disabled={sync.isPending}>
        {sync.isPending ? "Syncing…" : sync.data ? `Synced ${sync.data.synced}` : "Sync now"}
      </button>
      <button className="font-medium hover:text-ink" onClick={() => window.confirm("Stop putting your appointments on Google Calendar? Events already there stay.") && disconnect.mutate()}>
        Disconnect
      </button>
    </div>
  );
}

type Props = { items: AppointmentItem[]; colorOf: (a: AppointmentItem) => string; onOpen: (a: AppointmentItem) => void };

const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(":00", "").replace(" ", "").toLowerCase();
const onDay = (a: AppointmentItem, d: Date) => startOfDay(new Date(a.startsAt)) <= d && d <= startOfDay(new Date(a.endsAt));

function Chip({ a, color, onOpen }: { a: AppointmentItem; color: string; onOpen: (a: AppointmentItem) => void }) {
  const t = taskOf(a);
  if (t) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(a);
        }}
        onKeyDown={(e) => e.key === "Enter" && onOpen(a)}
        className="flex w-full cursor-pointer items-center gap-1 truncate rounded px-1 py-0.5 text-left text-xs hover:bg-mist"
        title={`Task: ${a.title}${dueTime(t) ? ` · due ${dueTime(t)}` : ""}`}
      >
        <TaskBox t={t} />
        {dueTime(t) && <span className="shrink-0 text-muted">{dueTime(t)}</span>}
        <span className={cx("truncate font-medium", t.completedAt && "text-muted line-through")}>{a.title}</span>
      </div>
    );
  }
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onOpen(a);
      }}
      className={cx("flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-xs hover:bg-mist", isExternal(a) && "text-ink-soft italic")}
      title={`${a.title} · ${appointmentWhen(a)}`}
    >
      <span className={cx("size-2 shrink-0 rounded-full", isExternal(a) && "border-2 bg-transparent")} style={isExternal(a) ? { borderColor: color } : { background: color }} />
      <WeatherFlag a={a} compact />
      {!a.allDay && <span className="shrink-0 text-muted">{time(a.startsAt)}</span>}
      <span className="truncate font-medium">{a.title}</span>
    </button>
  );
}

function MonthView({ anchor, from, items, colorOf, onOpen, onAdd, onDay: openDay }: Props & { anchor: Date; from: Date; onAdd?: (d: Date) => void; onDay: (d: Date) => void }) {
  const days = Array.from({ length: 42 }, (_, i) => addDays(from, i));
  const today = startOfDay(new Date());
  return (
    <Panel className="overflow-hidden">
      <div className="grid grid-cols-7 border-b border-mist text-center text-xs font-semibold tracking-wide text-muted uppercase">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const list = items.filter((a) => onDay(a, d));
          return (
            <div
              key={d.toISOString()}
              onClick={() => onAdd?.(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9))}
              className={cx("min-h-28 border-r border-b border-mist p-1 [&:nth-child(7n)]:border-r-0", d.getMonth() !== anchor.getMonth() && "bg-paper/70", onAdd && "cursor-pointer hover:bg-mist/30")}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openDay(d);
                }}
                className={cx("mb-0.5 grid size-6 place-items-center rounded-full text-xs font-semibold hover:bg-mist", sameDay(d, today) && "bg-brand text-white hover:bg-brand-deep", d.getMonth() !== anchor.getMonth() && "text-muted")}
              >
                {d.getDate()}
              </button>
              {list.slice(0, 3).map((a) => (
                <Chip key={a.id} a={a} color={colorOf(a)} onOpen={onOpen} />
              ))}
              {list.length > 3 && (
                <button
                  className="px-1 text-xs font-semibold text-muted hover:text-ink"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDay(d);
                  }}
                >
                  +{list.length - 3} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/** Week or day: hours down the side, one column per day; overlapping appointments share the column. */
function TimeGrid({ days, items, colorOf, onOpen, onAdd }: Props & { days: Date[]; onAdd?: (d: Date) => void }) {
  const hours = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);
  const today = startOfDay(new Date());
  const timed = items.filter((a) => !a.allDay);
  const allDay = items.filter((a) => a.allDay);
  return (
    <Panel className="overflow-x-auto">
      <div style={{ minWidth: days.length > 1 ? 720 : undefined }}>
        <div className="grid border-b border-mist" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className={cx("border-l border-mist px-2 py-2 text-center text-sm", sameDay(d, today) && "text-brand")}>
              <div className="text-xs font-semibold tracking-wide uppercase">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
              <div className="text-lg font-semibold">{d.getDate()}</div>
              <div className="flex flex-col">
                {allDay
                  .filter((a) => onDay(a, d))
                  .map((a) => (
                    <Chip key={a.id} a={a} color={colorOf(a)} onOpen={onOpen} />
                  ))}
              </div>
            </div>
          ))}
        </div>
        <div className="relative grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
          <div>
            {hours.map((h) => (
              <div key={h} className="pr-2 text-right text-xs text-muted" style={{ height: HOUR_PX }}>
                {new Date(2000, 0, 1, h).toLocaleTimeString("en-US", { hour: "numeric" })}
              </div>
            ))}
          </div>
          {days.map((d) => (
            <DayColumn key={d.toISOString()} day={d} hours={hours} items={timed.filter((a) => onDay(a, d))} colorOf={colorOf} onOpen={onOpen} onAdd={onAdd} />
          ))}
        </div>
      </div>
    </Panel>
  );
}

function DayColumn({ day, hours, items, colorOf, onOpen, onAdd }: Props & { day: Date; hours: number[]; onAdd?: (d: Date) => void }) {
  // Lay out overlapping appointments side by side.
  const laid = useMemo(() => {
    const sorted = [...items].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const cols: number[] = [];
    const out = sorted.map((a) => {
      const s = new Date(a.startsAt).getTime();
      let col = cols.findIndex((end) => end <= s);
      if (col === -1) col = cols.length;
      cols[col] = new Date(a.endsAt).getTime();
      return { a, col };
    });
    return out.map((x) => ({ ...x, of: Math.max(1, cols.length) }));
  }, [items]);
  const top = (iso: string) => {
    const d = new Date(iso);
    const clamped = d < day ? 0 : (d.getHours() + d.getMinutes() / 60 - DAY_START) * HOUR_PX;
    return Math.max(0, Math.min(clamped, hours.length * HOUR_PX));
  };
  return (
    <div className="relative border-l border-mist" style={{ height: hours.length * HOUR_PX }}>
      {hours.map((h) => (
        <div
          key={h}
          className={cx("border-b border-mist/70", onAdd && "cursor-pointer hover:bg-mist/30")}
          style={{ height: HOUR_PX }}
          onClick={(e) => {
            const half = e.nativeEvent.offsetY > HOUR_PX / 2 ? 30 : 0;
            onAdd?.(new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, half));
          }}
        />
      ))}
      {laid.map(({ a, col, of }) => {
        const t = top(a.startsAt);
        const end = sameDay(new Date(a.endsAt), day) ? top(a.endsAt) : hours.length * HOUR_PX;
        const color = colorOf(a);
        return (
          <button
            key={a.id}
            onClick={() => onOpen(a)}
            className={cx("absolute overflow-hidden rounded-md border-l-4 bg-white px-1.5 py-0.5 text-left text-xs shadow-sm ring-1 ring-mist hover:z-10 hover:ring-ink/30", isExternal(a) && "border-dashed italic shadow-none")}
            style={{ top: t, height: Math.max(end - t, 22), left: `calc(${(col / of) * 100}% + 2px)`, width: `calc(${100 / of}% - 4px)`, borderLeftColor: color, background: isExternal(a) ? `repeating-linear-gradient(135deg, white, white 6px, color-mix(in srgb, ${color} 8%, white) 6px, color-mix(in srgb, ${color} 8%, white) 12px)` : `color-mix(in srgb, ${color} 10%, white)` }}
          >
            <div className="flex gap-1 truncate font-semibold">
              <WeatherFlag a={a} compact />
              {a.title}
            </div>
            <div className="truncate text-muted">
              {time(a.startsAt)} · {a.attendees.map((x) => x.name.split(" ")[0]).join(", ")}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ListView({ items, colorOf, onOpen }: Props) {
  if (!items.length) {
    return (
      <Panel>
        <Empty>Nothing scheduled in the next 30 days.</Empty>
      </Panel>
    );
  }
  const byDay = new Map<string, AppointmentItem[]>();
  for (const a of items) {
    const key = toDateInput(a.startsAt);
    byDay.set(key, [...(byDay.get(key) ?? []), a]);
  }
  return (
    <Panel>
      {[...byDay.entries()].map(([day, list]) => (
        <div key={day} className="border-b border-mist last:border-0">
          <div className="bg-paper/70 px-5 py-1.5 text-sm font-semibold text-ink-soft">{new Date(`${day}T12:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          <ul>
            {list.map((a) => (
              <li key={a.id}>
                {taskOf(a) ? (
                  <ListTask a={a} t={taskOf(a)!} onOpen={onOpen} />
                ) : (
                <button className="flex w-full items-start gap-3 px-5 py-2.5 text-left hover:bg-mist/50" onClick={() => onOpen(a)}>
                  <span className="mt-1.5 size-2.5 shrink-0 rounded-full" style={{ background: colorOf(a) }} />
                  <span className="w-20 shrink-0 text-sm text-muted">{a.allDay ? "All day" : time(a.startsAt)}</span>
                  <span className="min-w-0">
                    <span className="block truncate font-semibold">{a.title}</span>
                    <span className="block truncate text-xs text-muted">{[a.jobNumber ? `Job #${a.jobNumber} ${a.contactName ?? ""}` : a.contactName, a.location, a.attendees.map((x) => x.name).join(", ")].filter(Boolean).join(" · ")}</span>
                    <WeatherFlag a={a} />
                  </span>
                </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Panel>
  );
}

function ListTask({ a, t, onOpen }: { a: AppointmentItem; t: TaskItem; onOpen: (a: AppointmentItem) => void }) {
  return (
    <div className="flex w-full items-start gap-3 px-5 py-2.5 hover:bg-mist/50">
      <span className="mt-1">
        <TaskBox t={t} />
      </span>
      <span className="w-20 shrink-0 text-sm text-muted">{dueTime(t) ? `Due ${dueTime(t)}` : "Task"}</span>
      <button className="min-w-0 text-left" onClick={() => onOpen(a)}>
        <span className={cx("block truncate font-semibold", t.completedAt && "text-muted line-through")}>{a.title}</span>
        <span className="block truncate text-xs text-muted">{[t.jobNumber ? `Job #${t.jobNumber} ${t.contactName ?? ""}` : t.contactName, a.attendees.map((x) => x.name).join(", ")].filter(Boolean).join(" · ")}</span>
      </button>
    </div>
  );
}

/** Saves the current view (type, people, layout) for everyone, optionally as some roles' default. */
function SaveViewDialog({ current, config, views, onClose, onSaved }: { current?: CalendarView; config: Pick<CalendarView, "view" | "types" | "userIds">; views: CalendarView[]; onClose: () => void; onSaved: (id: string) => void }) {
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles") });
  const [name, setName] = useState(current?.name ?? "");
  const [roleIds, setRoleIds] = useState<string[]>(current?.roleIds ?? []);
  const [asNew, setAsNew] = useState(!current);
  const save = useMutation({
    mutationFn: async (remove: boolean) => {
      const id = !asNew && current ? current.id : `v${Date.now().toString(36)}`;
      const mine: CalendarView = { id, name: name.trim(), ...config, roleIds };
      // A role has one default view: taking it here takes it off the others.
      const others = views.filter((v) => v.id !== id).map((v) => ({ ...v, roleIds: v.roleIds.filter((r) => !roleIds.includes(r)) }));
      await patch("/api/settings", { "calendar.views": remove ? views.filter((v) => v.id !== current?.id) : [...others, mine] });
      await qc.invalidateQueries({ queryKey: ["me"] });
      return remove ? "" : id;
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      title="Save calendar view"
      onClose={onClose}
      footer={
        <>
          {current && !asNew && (
            <Button variant="danger" className="mr-auto" busy={save.isPending} onClick={() => window.confirm(`Delete the ${current.name} view?`) && save.mutate(true)}>
              Delete view
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!name.trim()} onClick={() => save.mutate(false)}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <p className="text-sm text-ink-soft">
          Saves the layout ({config.view}), {config.types.length ? `these types: ${config.types.join(", ")}` : "all types"}, and {config.userIds.length ? `${config.userIds.length} people` : "everyone"} as a view anyone can pick.
        </p>
        {current && <Checkbox label={`Save as a new view (instead of changing ${current.name})`} checked={asNew} onChange={setAsNew} />}
        <Field label="Name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        {roles.data && (
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Opens first for</legend>
            <div className="grid sm:grid-cols-3">
              {roles.data.map((r) => (
                <Checkbox key={r.id} label={r.name} checked={roleIds.includes(r.id)} onChange={(on) => setRoleIds(on ? [...roleIds, r.id] : roleIds.filter((x) => x !== r.id))} />
              ))}
            </div>
          </fieldset>
        )}
      </div>
    </Dialog>
  );
}

/** Production: a row per sub, their scheduled work orders across two weeks. */
function CrewView({ from, to }: { from: Date; to: Date }) {
  const q = useQuery({ queryKey: ["work-orders", "crews", from.toISOString()], queryFn: () => get<WorkOrderListItem[]>(`/api/work-orders?status=all&from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`) });
  const days = Array.from({ length: 14 }, (_, i) => addDays(from, i));
  const today = startOfDay(new Date());
  const items = (q.data ?? []).filter((w) => w.startsAt && !["canceled", "declined", "draft"].includes(w.status));
  const rows = [...new Map(items.map((w) => [w.sub?.id ?? "none", w.sub?.name ?? "No sub picked"])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert>{errorMessage(q.error)}</Alert>;
  if (!rows.length) return <Panel><Empty>No crews scheduled these two weeks.</Empty></Panel>;
  const col = (d: Date) => Math.max(0, Math.min(13, Math.round((startOfDay(d).getTime() - from.getTime()) / 86_400_000)));
  return (
    <Panel className="overflow-x-auto">
      <div className="grid min-w-[900px]" style={{ gridTemplateColumns: "180px repeat(14, minmax(0, 1fr))" }}>
        <div className="border-b border-mist" />
        {days.map((d) => (
          <div key={d.toISOString()} className={cx("border-b border-l border-mist px-1 py-1.5 text-center text-xs", sameDay(d, today) ? "bg-brand/10 font-semibold text-brand" : d.getDay() === 0 || d.getDay() === 6 ? "bg-mist/60 text-muted" : "text-muted")}>
            {d.toLocaleDateString("en-US", { weekday: "short" })} {d.getDate()}
          </div>
        ))}
        {rows.map(([id, name]) => {
          const mine = items.filter((w) => (w.sub?.id ?? "none") === id);
          return (
            <div key={id} className="contents">
              <div className="flex items-center border-b border-mist px-3 py-2 text-sm font-semibold">{name}</div>
              <div className="relative grid border-b border-mist py-1" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))", gridColumn: "span 14" }}>
                {mine.map((w) => {
                  const s = col(new Date(w.startsAt!));
                  const e = col(new Date(w.endsAt ?? w.startsAt!));
                  return (
                    <Link
                      key={w.id}
                      to={`/jobs/${w.jobId}`}
                      title={`WO ${w.number} · ${w.jobLabel}${w.title ? ` · ${w.title}` : ""} (${w.status})`}
                      className={cx("m-0.5 truncate rounded px-1.5 py-1 text-xs font-medium", w.status === "confirmed" ? "bg-brand text-white" : w.status === "completed" ? "bg-ok/20 text-ink" : "bg-mist text-ink-soft")}
                      style={{ gridColumn: `${s + 1} / ${e + 2}` }}
                    >
                      #{w.jobNumber} {w.title || w.jobLabel}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="px-4 py-2 text-xs text-muted">Confirmed in red, waiting on the sub in gray, done in green. Pick a job to open it.</p>
    </Panel>
  );
}
